import dayjs from 'dayjs';
import * as echarts from 'echarts';
import React, { useEffect, useRef } from 'react';

// 迷你面积折线图组件 - 支持时间序列动态数据
export interface MiniAreaChartDataPoint {
  time: string | number; // 时间戳或时间字符串
  value: number; // 负载值
}

export interface MiniAreaChartProps {
  data?: MiniAreaChartDataPoint[];
  color: string;
  height?: number;
  maxDataPoints?: number; // 最多显示的数据点数量
}

const MiniAreaChart: React.FC<MiniAreaChartProps> = ({ 
  data = [], 
  color, 
  height = 60,
  maxDataPoints = 20,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // 初始化图表
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;

    // 处理数据 - 只保留最新的 maxDataPoints 个点
    const chartData = data.slice(-maxDataPoints);

    // 格式化时间和数值
    const times = chartData.map(item => {
      const time = typeof item.time === 'number' ? item.time : new Date(item.time).getTime();
      return dayjs(time).format('HH:mm:ss');
    });
    const values = chartData.map(item => item.value);

    const option: echarts.EChartsOption = {
      animation: true,
      animationDuration: 300,
      animationEasing: 'linear',
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const data = params[0];
          return `时间: ${data.axisValue}<br/>负载: ${data.value.toFixed(1)}%`;
        },
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderColor: 'transparent',
        textStyle: {
          color: '#fff',
          fontSize: 12,
        },
        padding: [8, 12],
        position: function (point: number[], params: any, dom: any, rect: any, size: any) {
          // point: [x, y] 鼠标位置
          // size: {contentSize: [width, height], viewSize: [width, height]} tooltip和图表的大小
          const [mouseX, mouseY] = point;
          const { contentSize, viewSize } = size;
          const [tooltipWidth, tooltipHeight] = contentSize;
          const [chartWidth, chartHeight] = viewSize;
          
          // 默认显示在鼠标右侧
          let x = mouseX + 10;
          let y = mouseY - tooltipHeight / 2;
          
          // 如果右侧空间不够，显示在左侧
          if (x + tooltipWidth > chartWidth) {
            x = mouseX - tooltipWidth - 10;
          }
          
          // 如果上方空间不够，向下调整
          if (y < 0) {
            y = 10;
          }
          
          // 如果下方空间不够，向上调整
          if (y + tooltipHeight > chartHeight) {
            y = chartHeight - tooltipHeight - 10;
          }
          
          return [x, y];
        },
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: color,
            width: 1,
            type: 'solid',
          },
        },
      },
      grid: {
        left: 0,
        right: 0,
        top: 8,
        bottom: 5,
      },
      xAxis: {
        type: 'category',
        data: times,
        show: false,
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        show: false,
        min: 0,
        max: function (value: any) {
          // 设置最大值为数据最大值的 1.1 倍，留出一些空间
          return Math.max(value.max * 1.1, 10);
        },
        scale: false,
      },
      series: [
        {
          type: 'line',
          data: values,
          smooth: true,
          symbol: 'none',
          sampling: 'lttb', // 降采样策略，提升性能
          lineStyle: {
            color: color,
            width: 1.5,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: color,
              },
              {
                offset: 1,
                color: 'rgba(255, 255, 255, 0.1)',
              },
            ]),
          },
        },
      ],
    };

    chart.setOption(option);

    // 响应式调整
    const handleResize = () => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, color, maxDataPoints]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return <div ref={chartRef} style={{ width: '100%', height: `${height}px` }} />;
};

export default MiniAreaChart;

